import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    maxVisiblePages?: number;
}

export default function Pagination({
    currentPage,
    totalPages,
    onPageChange,
    maxVisiblePages = 5,
}: PaginationProps) {
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];

        if (totalPages <= maxVisiblePages + 2) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            // Calculate range around current page
            let start = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
            const end = Math.min(totalPages - 1, start + maxVisiblePages - 1);

            // Adjust start if we're near the end
            if (end === totalPages - 1) {
                start = Math.max(2, end - maxVisiblePages + 1);
            }

            // Add ellipsis after first page if needed
            if (start > 2) {
                pages.push("...");
            }

            // Add page numbers in range
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Add ellipsis before last page if needed
            if (end < totalPages - 1) {
                pages.push("...");
            }

            // Always show last page
            pages.push(totalPages);
        }

        return pages;
    };

    const pages = getPageNumbers();

    if (totalPages === 0) {
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            {/* Previous Button */}
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-md bg-[#0F1623] border border-slate-700 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                <ChevronLeft size={18} className="text-slate-400" />
            </button>

            {/* Page Numbers */}
            {pages.map((page, index) => {
                if (page === "...") {
                    return (
                        <span key={`ellipsis-${index}`} className="px-3 py-2 text-slate-500">
                            ...
                        </span>
                    );
                }

                const pageNumber = page as number;
                const isActive = pageNumber === currentPage;

                return (
                    <button
                        key={pageNumber}
                        onClick={() => onPageChange(pageNumber)}
                        className={`min-w-[40px] px-3 py-2 rounded-md font-medium transition-all ${isActive
                                ? "bg-blue-600 text-white"
                                : "bg-[#0F1623] border border-slate-700 text-slate-300 hover:bg-slate-800"
                            }`}
                    >
                        {pageNumber}
                    </button>
                );
            })}

            {/* Next Button */}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md bg-[#0F1623] border border-slate-700 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                <ChevronRight size={18} className="text-slate-400" />
            </button>
        </div>
    );
}
